import { debounce } from 'lodash';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

const SET_PARAM_DEBOUNCE_TIME_MS = 50;

/** Read a string from URL Search Parameters */
export function useSearchParamState(key: string): [string, (value: string) => void];

/**
 * Store a string as state in URL Search Parameters
 *
 * Gives preference to already existing search parameter values on mount
 */
export function useSearchParamState(
  key: string,
  // eslint-disable-next-line @typescript-eslint/unified-signatures
  initialValue?: string | (() => string),
): [string, React.Dispatch<React.SetStateAction<string>>];

/**
 * Store a piece of serializable data in URL Search Parameters
 *
 * Gives preference to already existing search parameter values on mount
 * Takes a serializer/deserializer pair to manage non-string values
 */
export function useSearchParamState<T>(
  key: string,
  initialValue: T | (() => T),
  serde: {
    serializer: (value: T) => string;
    deserializer: (value: string) => T;
  },
): [T, React.Dispatch<React.SetStateAction<T>>];

/**
 * Store state in URL Search Parameters
 *
 * If the query parameter already exists, it uses that value, if not, it uses
 * an initializer
 *
 * Takes a serializer/deserializer pair to manage non-string values
 */

export function useSearchParamState<T>(
  key: string,
  initialValue?: T | (() => T),
  options?: {
    serializer: (value: T) => string;
    deserializer: (value: string) => T;
  },
): [T, (value: T | ((prev: T | null) => T)) => void] {
  const debouncedSetParamValues = React.useMemo(() => debounce(setParamValues, SET_PARAM_DEBOUNCE_TIME_MS), []);
  const [value, setValue] = React.useState<T | null>(() => {
    const existingParamValue = getParamValue(key);
    if (existingParamValue !== null) {
      if (options !== undefined) {
        return options.deserializer(existingParamValue);
      }
      return existingParamValue as T;
    } else if (initialValue !== undefined) {
      if (typeof initialValue === 'function') {
        return (initialValue as () => T)();
      }
      return initialValue as T;
    }
    return null;
  });

  // Keep serializer/deserializer reachable from effects without re-firing them
  // when the caller passes a freshly-created `options` object on every render.
  const optionsRef = React.useRef(options);
  optionsRef.current = options;

  /**
   * Sync state back from URL Search Parameters when the location changes
   *
   * This ensures that external navigation (e.g. `history.push` from an in-page
   * deep link) is reflected in component state. Writes from state to URL go
   * through `history.replaceState`, which does not trigger a react-router
   * location change, so this does not create a sync loop.
   */
  const { search } = useLocation();
  React.useEffect(() => {
    const paramValue = getParamValue(key);
    if (paramValue === null) {
      return;
    }
    const currentOptions = optionsRef.current;
    setValue(prev => {
      const serializedCurrent =
        prev === null
          ? null
          : currentOptions !== undefined
            ? currentOptions.serializer(prev)
            : (prev as unknown as string);
      if (serializedCurrent === paramValue) {
        return prev;
      }
      return currentOptions !== undefined ? currentOptions.deserializer(paramValue) : (paramValue as T);
    });
  }, [search, key]);

  /** Sync value to parameters on change */
  React.useEffect(() => {
    if (value !== null) {
      const existingValue = getParamValue(key);
      const currentOptions = optionsRef.current;
      const serializedValue = currentOptions !== undefined ? currentOptions.serializer(value) : (value as string);
      if (existingValue !== serializedValue) {
        debouncedSetParamValues({ [key]: serializedValue });
      }
    }

    // Cleanup: cancel any pending debounced calls when component unmounts or dependencies change
    return () => {
      debouncedSetParamValues.cancel();
    };
  }, [debouncedSetParamValues, key, value]);

  return [value as unknown as T, setValue];
}

export function useMultiSearchParamState<T extends Record<string, unknown>>(config: {
  [key in keyof T]: {
    initialValue?: T[key] | (() => T[key]);
    serde?: {
      serializer: (value: T[key]) => string;
      deserializer: (value: string) => T[key];
    };
    paramKey?: string;
  };
}) {
  const debouncedSetParamValues = React.useMemo(() => debounce(setParamValues, SET_PARAM_DEBOUNCE_TIME_MS), []);
  const [value, setValue] = React.useState<{ [key in keyof T]: T[key] | null }>(() => {
    const fullInitialValue: { [key in keyof T]: T[key] | null } = {} as T;
    for (const key in config) {
      const { initialValue, serde, paramKey } = config[key];
      const existingParamValue = getParamValue(paramKey ?? key);
      if (existingParamValue !== null) {
        if (serde !== undefined) {
          fullInitialValue[key] = serde.deserializer(existingParamValue);
        } else {
          fullInitialValue[key] = existingParamValue as T[typeof key];
        }
      } else if (initialValue !== undefined) {
        if (typeof initialValue === 'function') {
          fullInitialValue[key] = (initialValue as () => T[typeof key])();
        } else {
          fullInitialValue[key] = initialValue as T[typeof key];
        }
      } else {
        fullInitialValue[key] = null;
      }
    }
    return fullInitialValue;
  });

  // Keep config reachable from the URL-sync effect without re-firing it when
  // the caller passes a fresh config object on every render.
  const configRef = React.useRef(config);
  configRef.current = config;

  /**
   * Sync state back from URL Search Parameters when the location changes
   *
   * See note on the equivalent effect in `useSearchParamState` for details.
   */
  const { search } = useLocation();
  React.useEffect(() => {
    const currentConfig = configRef.current;
    setValue(prev => {
      let changed = false;
      const next = { ...prev };
      for (const key in currentConfig) {
        const { serde, paramKey } = currentConfig[key];
        const existingParamValue = getParamValue(paramKey ?? key);
        if (existingParamValue === null) {
          continue;
        }
        const prevValue = prev[key];
        const prevSerialized =
          prevValue === null
            ? null
            : serde !== undefined
              ? serde.serializer(prevValue as T[typeof key])
              : (prevValue as unknown as string);
        if (prevSerialized !== existingParamValue) {
          next[key] =
            serde !== undefined ? serde.deserializer(existingParamValue) : (existingParamValue as T[typeof key]);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [search]);

  /** Sync value to parameters on change */
  React.useEffect(() => {
    const convertedObj: { [key: string]: string } = {};
    for (const key in value) {
      const keyValue = value[key];
      if (keyValue === null) {
        continue;
      }
      const { serde, paramKey } = config[key];
      const existingValue = getParamValue(paramKey ?? key);
      const serializedValue = serde !== undefined ? serde.serializer(keyValue) : (keyValue as string);
      if (existingValue !== serializedValue) {
        convertedObj[paramKey ?? key] = serializedValue;
      }
    }
    if (Object.keys(convertedObj).length > 0) {
      debouncedSetParamValues(convertedObj);
    }

    // Cleanup: cancel any pending debounced calls when component unmounts or dependencies change
    return () => {
      debouncedSetParamValues.cancel();
    };
  }, [config, debouncedSetParamValues, value]);

  return [
    value as unknown as T,
    setValue as React.Dispatch<React.SetStateAction<{ [key in keyof T]: T[key] }>>,
  ] as const;
}

function getParamValue(param: string): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get(param);
}

function setParamValues(obj: { [paramKey: string]: string }) {
  const searchParams = new URLSearchParams(window.location.search);
  for (const [key, value] of Object.entries(obj)) {
    searchParams.set(key, value);
  }
  window.history.replaceState(null, '', `${window.location.pathname}?${searchParams.toString()}`);
}
